import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import About from "../About/About";
import Products from "../Products/Products";
import Services from "../Services/Services";
import WordsFromCeo from "./WordsFromCeo";
import CardBasic from "@/components/elements/Card/CardBasic";
import { Carousel } from "@/components/elements/Carousel/Carousel";
import FadeInSection from "@/components/wrappers/FadeInSection";
import ButtonElement from "@/components/elements/Button/Button";
import MapEmbed from "@/components/elements/MapEmbed/MapEmbed";


const fadeInUp = {
  initial: { opacity: 0, y: 50 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
  viewport: { once: false },
};

const fadeInLeft = {
  initial: { opacity: 0, x: -100 },
  whileInView: { opacity: 1, x: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
  viewport: { once: false },
};

const Home = () => {

  const [scrolled, setScrolled] = useState(false);

  // State to track errors for each field
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    phone?: string;
    message?: string;
  }>({});

  const handleScroll = () => {
    setScrolled(window.scrollY > 50);
  };

  // New handleSubmit with AJAX + validation
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setErrors({});

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const nameInput = formData.get("name")?.toString().trim() || "";
    const emailInput = formData.get("email")?.toString().trim() || "";
    const phoneInput = formData.get("phone")?.toString().trim() || "";
    const messageInput = formData.get("message")?.toString().trim() || "";

    const newErrors: typeof errors = {};

    if (!nameInput) newErrors.name = "Please fill in this field";
    if (!emailInput) newErrors.email = "Please fill in this field";
    else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(emailInput)) newErrors.email = "Please enter a valid email address";
    }
    if (!phoneInput) {
      newErrors.phone = "Please fill in this field";
    } else {
      const englishNumberPattern = /^[0-9]{10}$/;
      if (!englishNumberPattern.test(phoneInput)) newErrors.phone = "Please enter a valid 10-digit phone number";
    }
    if (!messageInput) newErrors.message = "Please fill in this field";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await fetch("https://formspree.io/f/xoqgejwg", {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: formData,
      });

      if (response.ok) {
        alert("Thank you for your message! We will get back to you soon.");
        form.reset();
        setErrors({});
      } else {
        const data = await response.json();
        if (data.errors) {
          const apiErrors: typeof errors = {};
          data.errors.forEach((error: { field: string; message: string }) => {
            apiErrors[error.field] = error.message;
          });
          setErrors(apiErrors);
        } else {
          alert("Oops! Something went wrong. Please try again.");
        }
      }
    } catch (error) {
      alert("Network error. Please check your connection and try again.");
    }
  };

    useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <div>
        {/* Carousel Section */}
        <div className="responsive-margin">
          <Carousel />
        </div>

        {/* About Section */}
        <div className="responsive-padding">
          <motion.div {...fadeInLeft}>
            <About />
          </motion.div>

          {/* Cards Section */}
          <section
            id="cards"
            className="pt-10 flex justify-center gap-10 flex-wrap text-white sm:px-5 md:px-20 lg:px-[7.5rem] xl:px-[8.75rem] 2xl:px-40"
          >
            <motion.div {...fadeInUp}>
              <div className="flex flex-wrap justify-center gap-6">
                <div className="card-container">
                  <CardBasic cardTitle="10 +" cardDescription="Years of Experience" />
                </div>
                <div className="card-container">
                  <CardBasic cardTitle="50 +" cardDescription="Project Completed" />
                </div>
                <div className="card-container">
                  <CardBasic cardTitle="200 +" cardDescription="Satisfied Clients" />
                </div>
                <div className="card-container">
                  <CardBasic cardTitle="20 +" cardDescription="Active Workers" />
                </div>
              </div>
            </motion.div>
          </section>

          {/* Services Section */}
          <motion.div {...fadeInLeft}>
            <div>
              <Services showLimited={true} />
            </div>
          </motion.div>

          {/* Products Section */}
          <motion.div {...fadeInLeft}>
            <div>
              <Products showDropdown={false} />
            </div>
          </motion.div>

          {/* Words from CEO Section */}
          <section className="bg-custom-pgcolor py-10 bg-opacity-30">
            <FadeInSection direction="left" delay={0.15}>
              <WordsFromCeo />
            </FadeInSection>
          </section>

          {/* Contact Us Section */}
          <div className="sm:px-2 md:px-8 lg:px-12 xl:px-16 2xl:px-20 my-10">
            <section id="contact-section" className="mt-[4rem] px-2 md:px-8 lg:px-12 xl:px-16 2xl:px-20">
              <div className="sm:px-2 md:px-8 lg:px-12 xl:px-16 2xl:px-20">
                <motion.div
                  className="custom__divider"
                  initial={{ opacity: 0, scaleX: 0 }}
                  animate={{ opacity: 1, scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 0.15 }}
                >
                  <div className="text-container">
                    <h2 className="text-custom-secolor font-extrabold text-left title-text-size">Contact Us</h2>
                  </div>
                </motion.div>
              </div>

              <div className="bg-background mb-4 sm:px-4 md:px-8 lg:px-12 xl:px-16 2xl:px-20">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-stretch gap-6 mb-2">
                  {/* Map Section */}
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut", delay: 0.15 }}
                    className="w-full lg:w-1/2 flex-grow"
                  >
                    <MapEmbed />
                  </motion.div>

                  {/* Form Section */}
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                    className="w-full lg:w-1/2 flex justify-start items-start flex-grow"
                  >
                    <div className="w-full px-2 sm:px-4 max-w-[clamp(300px, 100%, 600px)]">
                      <motion.h3
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                        className="text-custom-secolor lg:text-center pb-4 animate-pulse title-text-size font-extrabold text-left"
                      >
                        Enquire Now
                      </motion.h3>

                      <motion.form
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, ease: "easeOut", delay: 0.5 }}
                        id="contact-form"
                        onSubmit={handleSubmit} // ajax submit with validation
                        className="flex flex-col gap-4 w-full"
                        noValidate
                      >
                        <div>
                          <input
                            id="name-input"
                            name="name"
                            type="text"
                            placeholder="Enter Name"
                            className={`bg-white input input-bordered w-full content-text-size ${
                              errors.name ? "border-red-500" : ""
                            }`}
                          />
                          {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
                        </div>

                        <div>
                          <input
                            id="email-input"
                            type="email"
                            name="email"
                            placeholder="Enter Email"
                            className={`bg-white input input-bordered w-full content-text-size ${
                              errors.email ? "border-red-500" : ""
                            }`}
                          />
                          {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
                        </div>

                        <div>
                          <input
                            id="phone-input"
                            type="tel"
                            name="phone"
                            placeholder="Enter Phone Number"
                            pattern="[0-9]{10}"
                            className={`bg-white input input-bordered w-full content-text-size ${
                              errors.phone ? "border-red-500" : ""
                            }`}
                          />
                          {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone}</p>}
                        </div>

                        <div>
                          <textarea
                            id="message-input"
                            name="message"
                            placeholder="Enter Message"
                            rows={4}
                            className={`bg-white textarea textarea-bordered w-full content-text-size ${
                              errors.message ? "border-red-500" : ""
                            }`}
                          />
                          {errors.message && <p className="text-red-600 text-sm mt-1">{errors.message}</p>}
                        </div>

                        <ButtonElement
                          btnText="Send Message"
                          onClick={() => {}}
                          className="h1 font-bold hover-text-responsive transition-all ease-in-out duration-500  h-16 rounded-lg"
                          size="default"
                          type="submit"
                        />
                      </motion.form>
                    </div>
                  </motion.div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
